import type { AuthedRequest } from '../auth/AuthProvider';

/** Mirrors the public contract of /api/v1/users and /api/v1/roles. */
export interface RoleRef {
  id: string;
  key: string;
  name: string;
}

/** Who a profile is for (ADR-027). */
export type RoleKind = 'org' | 'client' | 'account' | 'integration';

export interface Role extends RoleRef {
  description: string;
  is_system: boolean;
  kind: RoleKind;
  /** Built-in profiles keep their name and cannot be deleted. */
  builtin: boolean;
  /** The administrator: every permission, always. */
  locked: boolean;
  /** Users holding the profile. */
  users: number;
  permissions: string[];
}

/** A row of the permission table. */
export interface PermissionInfo {
  key: string;
  area: string;
  label: string;
  /** Profile kinds that may hold it. */
  kinds: RoleKind[];
}

export const kindLabels: Record<RoleKind, string> = {
  org: 'Equipe',
  client: 'Cliente',
  account: 'Conta',
  integration: 'Sistema',
};

/** Profiles for people of a client: they see only their client's cameras. */
export const isClientKind = (k: RoleKind | undefined) => k === 'client' || k === 'account';

export type UserStatus = 'active' | 'disabled';

export interface Member {
  id: string;
  email: string;
  display_name: string;
  status: UserStatus;
  last_login_at: string | null;
  roles: RoleRef[];
  /** The client people of a client belong to (ADR-027). */
  client?: { id: string; name: string } | null;
  /** Has not accepted the e-mailed invitation yet (ADR-030). */
  invited?: boolean;
}

export interface MemberList {
  items: Member[];
  total: number;
  limit: number;
  offset: number;
}

export interface UserQuery {
  q: string;
  status: '' | UserStatus;
  role: string;
  sort: string;
  page: number;
  size: number;
}

export interface CreateUserBody {
  email: string;
  display_name: string;
  /** Required unless invite is set. */
  password?: string;
  roles: string[];
  client_id?: string;
  /** E-mails an invitation: the person confirms a code and chooses the password (ADR-030). */
  invite?: boolean;
}

export interface UpdateUserBody {
  display_name?: string;
  status?: UserStatus;
  roles?: string[];
  password?: string;
  client_id?: string;
}

export const usersKey = ['users'] as const;
export const rolesKey = ['roles'] as const;

export function listUsers(req: AuthedRequest, q: UserQuery): Promise<MemberList> {
  const params = new URLSearchParams({
    limit: String(q.size),
    offset: String((q.page - 1) * q.size),
    sort: q.sort,
  });
  if (q.q) params.set('q', q.q);
  if (q.status) params.set('status', q.status);
  if (q.role) params.set('role', q.role);
  return req<MemberList>('GET', `/api/v1/users?${params.toString()}`);
}

export async function listRoles(req: AuthedRequest): Promise<Role[]> {
  return (await req<{ items: Role[] }>('GET', '/api/v1/roles')).items;
}

export const permissionsKey = ['permissions'] as const;

export async function listPermissions(req: AuthedRequest): Promise<PermissionInfo[]> {
  return (await req<{ items: PermissionInfo[] }>('GET', '/api/v1/permissions')).items;
}

export interface CreateRoleBody {
  name: string;
  description?: string;
  /** Profile whose kind and permissions the new one starts with. */
  from: string;
}

export interface UpdateRoleBody {
  name?: string;
  description?: string;
  permissions?: string[];
}

export const createRole = (req: AuthedRequest, body: CreateRoleBody) =>
  req<Role>('POST', '/api/v1/roles', body);

export const updateRole = (req: AuthedRequest, id: string, body: UpdateRoleBody) =>
  req<Role>('PATCH', `/api/v1/roles/${encodeURIComponent(id)}`, body);

export const deleteRole = (req: AuthedRequest, id: string) =>
  req<null>('DELETE', `/api/v1/roles/${encodeURIComponent(id)}`);

export function createUser(req: AuthedRequest, body: CreateUserBody): Promise<Member> {
  return req<Member>('POST', '/api/v1/users', body);
}

/** Sends a new invitation to a user who has not accepted one (voids the previous link). */
export function resendInvite(req: AuthedRequest, id: string): Promise<Member> {
  return req<Member>('POST', `/api/v1/users/${encodeURIComponent(id)}/invite`, {});
}

export function updateUser(req: AuthedRequest, id: string, body: UpdateUserBody): Promise<Member> {
  return req<Member>('PATCH', `/api/v1/users/${encodeURIComponent(id)}`, body);
}

const messages: Record<string, Record<string, string>> = {
  email: {
    invalid: 'Informe um e-mail válido.',
    taken: 'Este e-mail já está em uso nesta organização.',
  },
  display_name: { invalid: 'Informe um nome com até 200 caracteres.' },
  password: {
    too_short: 'A senha deve ter pelo menos 12 caracteres.',
    too_long: 'A senha é longa demais.',
    use_change_password: 'Para alterar a sua própria senha, use "Alterar senha" no menu da conta.',
  },
  new_password: {
    too_short: 'A nova senha deve ter pelo menos 12 caracteres.',
    too_long: 'A nova senha é longa demais.',
    same_as_current: 'A nova senha deve ser diferente da atual.',
  },
  current_password: { required: 'Informe a senha atual.', incorrect: 'Senha atual incorreta.' },
  roles: {
    required: 'Escolha o perfil do usuário.',
    unknown: 'O perfil escolhido não existe mais.',
    one_profile: 'Cada usuário tem um único perfil.',
    not_held: 'Você não pode dar um perfil com permissões que você mesmo não tem.',
    self_demote: 'Você não pode tirar o seu próprio perfil de administrador.',
    not_for_client: 'Pessoas do cliente só recebem perfis de cliente.',
  },
  client_id: {
    required: 'Escolha o cliente desta pessoa.',
    unknown: 'O cliente escolhido não existe mais.',
    invalid: 'Você não pode mudar o cliente desta pessoa.',
  },
  name: {
    invalid: 'Informe um nome com até 60 caracteres.',
    taken: 'Já existe um perfil com este nome.',
    builtin: 'Os perfis padrão não podem ser renomeados.',
  },
  description: { too_long: 'A descrição deve ter até 300 caracteres.' },
  permissions: {
    locked: 'As permissões do administrador geral não podem ser reduzidas.',
    not_held: 'Você só pode marcar ou desmarcar permissões que você mesmo tem.',
    not_for_kind: 'Uma das permissões não vale para este tipo de perfil.',
    unknown: 'Uma das permissões não existe mais. Recarregue a página.',
  },
  from: { unknown: 'O perfil de origem não existe mais.', invalid: 'Escolha o perfil de origem.' },
  status: { self_disable: 'Você não pode desativar a sua própria conta.' },
};

/** Translates API field error codes into operator-facing text. */
export function fieldMessages(fields: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [field, code] of Object.entries(fields)) {
    out[field] = messages[field]?.[code] ?? 'Valor inválido.';
  }
  return out;
}

/** Which cameras a user sees (ADR-019): every device, or the assigned ones. */
export type AccessMode = 'all' | 'assigned';

export interface UserAccess {
  mode: AccessMode;
  /** A client grant covers all its accounts, including later ones (ADR-026). */
  client_ids?: string[];
  /** A site grant covers every device in it, including future ones. */
  site_ids: string[];
  /** A recorder grant covers its channels. */
  device_ids: string[];
}

export const accessKey = (id: string) => ['user-access', id] as const;

export const getAccess = (req: AuthedRequest, id: string) =>
  req<UserAccess>('GET', `/api/v1/users/${encodeURIComponent(id)}/access`);

export const putAccess = (req: AuthedRequest, id: string, body: UserAccess) =>
  req<UserAccess>('PUT', `/api/v1/users/${encodeURIComponent(id)}/access`, body);

export const accessMessages: Record<string, string> = {
  admin_all: 'Administradores sempre veem todas as câmeras.',
  whole_client: 'Este perfil já vê todas as contas do cliente.',
  client_assigned: 'Pessoas do cliente veem só o que for liberado.',
  outside_client: 'Só é possível liberar contas e câmeras do cliente desta pessoa.',
  unknown: 'Um local ou câmera selecionado não existe mais. Recarregue e tente de novo.',
  out_of_range: 'Selecione no máximo 1000 locais e câmeras.',
};
